import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { marginFields, visibilityField, weightField } from '../_shared/blockFields.js';
import { marginStyle, visibilityCss, num, str, readWeight } from '../_shared/blockRender.js';
import { resolveColor } from '../../sections/_shared/settingValue.js';
import { fixedWidthImgProps } from '../_shared/assetVariant.js';
function Stars({ rating, color }) {
    const full = Math.round(Math.max(0, Math.min(5, rating)));
    return (_jsxs("span", { "aria-label": `${rating} / 5`, style: { color, letterSpacing: '1px', lineHeight: 1 }, children: ['★'.repeat(full), _jsx("span", { style: { color: 'color-mix(in srgb, currentColor 22%, transparent)' }, children: '★'.repeat(5 - full) })] }));
}
export const reviewBlockSchema = {
    type: 'review',
    name: 'Review',
    fields: [
        { id: 'avatar', type: 'image', label: 'Avatar' },
        {
            id: 'quote',
            type: 'textarea',
            label: 'Quote',
            default: 'Absolutely love it — highly recommend!',
            defaultKey: 'section.review_quote',
            // ÉCRITE PAR L'IA : c'est la parole d'une cliente, tirée des avis et traduite. L'auteur, lui,
            // reste au thème et sera EFFACÉ à la composition — le corpus ne porte aucun nom, et en
            // inventer un sous une vraie phrase fabrique un témoin.
            aiWritable: true,
        },
        {
            id: 'author',
            type: 'text',
            label: 'Author',
            default: 'Sarah M., US',
            defaultKey: 'section.review_author',
        },
        { id: 'showStars', type: 'checkbox', label: 'Show stars', aiConfigurable: true, default: true },
        { id: 'rating', type: 'range', label: 'Stars', min: 0, max: 5, step: 0.5, default: 5 },
        {
            id: 'starColor',
            type: 'color',
            label: 'Stars color',
            default: { ref: 'theme', token: 'star' },
        },
        { id: 'italic', type: 'checkbox', label: 'Italic quote', aiConfigurable: true, default: true },
        weightField,
        // Layout controls — so the avatar/quote spacing is tunable (fixes "the quote floats above the image").
        {
            id: 'avatarSize',
            type: 'range',
            label: 'Avatar size',
            group: 'Layout',
            min: 32,
            max: 96,
            step: 2,
            default: 52,
            unit: 'px',
        },
        {
            id: 'gap',
            type: 'range',
            label: 'Gap (avatar → text)',
            group: 'Layout',
            min: 4,
            max: 28,
            step: 2,
            default: 12,
            unit: 'px',
        },
        {
            id: 'align',
            aiConfigurable: true,
            type: 'select',
            label: 'Vertical align',
            group: 'Layout',
            default: 'center',
            options: [
                { value: 'center', label: 'Center' },
                { value: 'top', label: 'Top' },
            ],
        },
        ...marginFields(4, 12),
        visibilityField,
    ],
};
const ReviewBlock = ({ block }) => {
    const bs = block.settings;
    const cls = `aqx-rv-${block.id}`;
    const avatar = (bs.avatar ?? {});
    const author = str(bs.author, '');
    const quote = str(bs.quote, '');
    const showStars = bs.showStars !== false;
    const rating = num(bs.rating, 5);
    // Fallback MUST equal the schema default (`{ref:'theme',token:'star'}`) so the rendered colour matches
    // the inspector swatch when no explicit colour is stored — same as the `rating` block's `#ffcc00`.
    const starColor = resolveColor(bs.starColor, 'var(--aqx-theme-star, #ffcc00)');
    const avatarSize = num(bs.avatarSize, 52);
    const gap = num(bs.gap, 12);
    const align = str(bs.align, 'center');
    const weight = readWeight(bs);
    const style = {
        ...marginStyle(bs, 4, 12),
        '--rv-av': `${avatarSize}px`,
        '--rv-gap': `${gap}px`,
    };
    return (_jsxs("div", { className: cls, style: style, children: [_jsx("style", { dangerouslySetInnerHTML: { __html: css(cls, weight) + visibilityCss(cls, bs.visibility) } }), _jsxs("div", { className: "aqx-rv-row", style: { alignItems: align === 'top' ? 'flex-start' : 'center' }, children: [avatar.url ? (_jsx("img", { className: "aqx-rv-av", ...fixedWidthImgProps(avatar.url, avatarSize), alt: avatar.alt || author, loading: "lazy", decoding: "async" })) : null, _jsxs("div", { className: "aqx-rv-body", children: [_jsx("p", { className: "aqx-rv-quote", style: { fontStyle: bs.italic === false ? 'normal' : 'italic' }, children: quote }), _jsxs("div", { className: "aqx-rv-meta", children: [author ? _jsx("span", { className: "aqx-rv-author", children: author }) : null, showStars ? _jsx(Stars, { rating: rating, color: starColor }) : null] })] })] })] }));
};
function css(cls, weight = '') {
    const c = `.${cls}`;
    // PRIMARY text = the quote. Only append font-weight when a weight is set, so an unset weight renders
    // byte-for-byte identically to before (zero migration).
    const quoteWeight = weight ? `;font-weight:${weight}` : '';
    return (`${c} .aqx-rv-row{display:flex;gap:var(--rv-gap,12px)}` +
        `${c} .aqx-rv-av{inline-size:var(--rv-av,52px);block-size:var(--rv-av,52px);flex:none;border-radius:50%;object-fit:cover;` +
        `border:1px solid var(--aqx-scheme-border,#e4e4e9)}` +
        `${c} .aqx-rv-body{min-inline-size:0}` +
        `${c} .aqx-rv-quote{margin:0;font-size:14px;line-height:1.45;color:var(--aqx-scheme-text,#1b1b1e)${quoteWeight}}` +
        `${c} .aqx-rv-meta{display:flex;align-items:center;gap:8px;margin-block-start:4px;flex-wrap:wrap}` +
        `${c} .aqx-rv-author{font-weight:700;font-size:13px;color:var(--aqx-scheme-text,#1b1b1e)}`);
}
export const reviewBlock = {
    type: 'review',
    schema: reviewBlockSchema,
    Component: ReviewBlock,
    meta: { label: 'Review' },
};
//# sourceMappingURL=index.js.map